import { ProgramDay } from '../enums';
import { z } from 'zod';

import { CoverSourceName, MediaSourceValue } from './asset-source';
import { Translation } from './translation';

const ProgramWeekDto = z
  .object({
    weekIndex: z.number().nonnegative(),
    weekName: z.string().nullish(),
    day: z.enum([
      ProgramDay.MONDAY,
      ProgramDay.TUESDAY,
      ProgramDay.WEDNESDAY,
      ProgramDay.THURSDAY,
      ProgramDay.FRIDAY,
      ProgramDay.SATURDAY,
      ProgramDay.SUNDAY,
    ]),
    workoutId: z.string().uuid(),
    // Substitute workout for this slot. Progress/schedule stay anchored to
    // workoutId (the primary); this only registers a stand-in the client
    // can do instead.
    alternateWorkoutId: z.string().uuid().optional().nullable(),
  })
  .superRefine((week, ctx) => {
    if (week.alternateWorkoutId === week.workoutId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__AlternateWorkoutDifferent',
        path: ['alternateWorkoutId'],
      });
    }
  });

export const CreateProgramDto = z.object({
  mediaType: z.string().optional(),
  mediaSource: MediaSourceValue.optional(),
  coverSource: CoverSourceName,
  coverType: z.string(),
  organizationId: z.string().uuid().nullish(),
  level: z
    .number()
    .min(0, 'VALIDATION__NonNegative')
    .max(5, 'VALIDATION__MaxValue5'),
  tags: z.array(z.string()).optional(),
  translations: z.array(Translation),
  weeks: z.array(ProgramWeekDto),
  configuration: z
    .object({
      organizationIds: z.array(z.string()).optional(),
      marketplace: z
        .object({
          enabled: z.boolean(),
          price: z.number().nonnegative().optional(),
          currency: z.string().optional(),
        })
        .optional(),
      isOrg: z.boolean().optional(),
    })
    .optional(),
});

export type ApiCreateProgramDto = z.infer<typeof CreateProgramDto>;

export const UpdateProgramDto = z
  .object({
    mediaType: z.string().optional(),
    mediaSource: MediaSourceValue.optional(),
    coverSource: CoverSourceName.optional().nullable(),
    coverType: z.string().optional().nullable(),
    level: z
      .number()
      .min(0, 'VALIDATION__NonNegative')
      .max(5, 'VALIDATION__MaxValue5')
      .optional(),
    tags: z.array(z.string()).optional(),
    translations: z.array(Translation).optional(),
    weeks: z.array(ProgramWeekDto).optional(),
    configuration: z
      .object({
        marketplace: z
          .object({
            enabled: z.boolean(),
            price: z.number().nonnegative().optional(),
            currency: z.string().optional(),
          })
          .optional(),
        isOrg: z.boolean().optional(),
      })
      .optional(),
  })
  .strict();

export type ApiUpdateProgramDto = z.infer<typeof UpdateProgramDto>;

export const ValidationProgramDto = z
  .object({
    mediaType: z.string().optional(),
    mediaSource: MediaSourceValue.optional(),
    coverSource: CoverSourceName.optional(),
    coverType: z.string().optional(),
    level: z
      .number()
      .min(0, 'VALIDATION__NonNegative')
      .max(5, 'VALIDATION__MaxValue5')
      .optional(),
    tags: z.array(z.string()).optional(),
    translations: z.array(Translation).optional(),
    weeks: z.array(ProgramWeekDto).optional(),
    configuration: z
      .object({
        organizationIds: z.array(z.string()).optional(),
        marketplace: z
          .object({
            enabled: z.boolean(),
            price: z.number().nonnegative().optional(),
            currency: z.string().optional(),
          })
          .optional(),
        isOrg: z.boolean().optional(),
      })
      .optional(),
  })
  .strict();

export type ApiValidationProgramDto = z.infer<typeof ValidationProgramDto>;
